import type { ReactNode } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"

export interface DataListColumn<T> {
  key: string
  header: ReactNode
  cell: (row: T, index: number) => ReactNode
  headClassName?: string
  cellClassName?: string
}

interface DataListProps<T> {
  rows: T[]
  columns: DataListColumn<T>[]
  rowKey: (row: T, index: number) => string | number
  mobileItem: (row: T, index: number) => ReactNode
  emptyState: ReactNode
  className?: string
}

export function DataList<T>({
  rows,
  columns,
  rowKey,
  mobileItem,
  emptyState,
  className,
}: DataListProps<T>) {
  if (rows.length === 0) {
    return <>{emptyState}</>
  }

  return (
    <div className={className}>
      {/* Desktop table */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key} className={col.headClassName}>
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={rowKey(row, index)}>
                {columns.map((col) => (
                  <TableCell key={col.key} className={col.cellClassName}>
                    {col.cell(row, index)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <ul className={cn("flex flex-col gap-2 md:hidden")}>
        {rows.map((row, index) => (
          <li key={rowKey(row, index)} data-slot="data-list-item">
            {mobileItem(row, index)}
          </li>
        ))}
      </ul>
    </div>
  )
}
